import { useState } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { QuinnBusinessOption, QuinnCopy, QuinnScope, QuinnScopeMode } from "../../types";
import { ALL_CLIENTS_LABEL } from "../../utils/constants.ts";
import { ScopeChip } from "./ScopeChip.tsx";
import { ScopeOptionRow } from "./ScopeOptionRow.tsx";

type QuinnScopeChipsProps = {
    scope: QuinnScope;
    businesses: QuinnBusinessOption[];
    isLoading: boolean;
    canAskAllClients: boolean;
    onChange: (patch: Partial<QuinnScope>) => void;
};

export function QuinnScopeChips({
    scope,
    businesses,
    isLoading,
    canAskAllClients,
    onChange,
}: QuinnScopeChipsProps) {
    const [search, setSearch] = useState("");

    const filteredBusinesses = businesses.filter((business) =>
        business.name.toLowerCase().includes(search.trim().toLowerCase()),
    );

    return (
        <div className="flex flex-wrap gap-1.5">
            <ScopeChip
                label="Klant"
                value={isLoading ? QuinnCopy.LoadingClients : scope.businessLabel}
                isActive={scope.mode === QuinnScopeMode.AllClients || Boolean(scope.businessId)}
                isDisabled={isLoading}
            >
                {(close) => (
                    <>
                        <div className="flex items-center gap-1.5 border-b border-gray-100 px-2 py-1.5">
                            <MagnifyingGlassIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            <input
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Zoek klant"
                                className="w-full border-0 bg-transparent p-0 text-xs text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-0"
                            />
                        </div>
                        <div className="max-h-56 overflow-y-auto py-1">
                            {canAskAllClients && (
                                <ScopeOptionRow
                                    label={ALL_CLIENTS_LABEL}
                                    isSelected={scope.mode === QuinnScopeMode.AllClients}
                                    onSelect={() => {
                                        onChange({
                                            mode: QuinnScopeMode.AllClients,
                                            businessId: null,
                                            businessLabel: ALL_CLIENTS_LABEL,
                                        });
                                        close();
                                    }}
                                />
                            )}
                            {filteredBusinesses.map((business) => (
                                <ScopeOptionRow
                                    key={business.id}
                                    label={business.name}
                                    isSelected={
                                        scope.mode === QuinnScopeMode.Client && scope.businessId === business.id
                                    }
                                    onSelect={() => {
                                        onChange({
                                            mode: QuinnScopeMode.Client,
                                            businessId: business.id,
                                            businessLabel: business.name,
                                        });
                                        close();
                                    }}
                                />
                            ))}
                            {filteredBusinesses.length === 0 && (
                                <p className="px-2 py-2 text-xs text-gray-400">Geen klanten gevonden</p>
                            )}
                        </div>
                    </>
                )}
            </ScopeChip>
        </div>
    );
}
